import { useStore } from '../store/index'
import { apiSolicitudes } from './useSolicitudWeb'

/** Notaría participante, tal como la devuelve el directorio público de minotaria.es. */
export interface NotariaMinotaria {
  id: number
  nombre: string
  direccion: string | null
  cp: string | null
  municipio: string | null
  provincia: string | null
  recaptchaGroup: number | null
  /** Próxima fecha para firmar en persona ("aaaa-mm-dd"), si la notaría la enseña. Orientativa. Ref: https://trello.com/c/quriKGDT */
  proximaFecha?: string | null
}

/** "jueves 5 de noviembre" (o "jue. 5 nov." en corto) a partir de "aaaa-mm-dd". Vacío si la fecha no es válida. */
export function textoFechaFirma(iso: string | null | undefined, corto = false): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso ?? '')
  if (!m) return ''
  const fecha = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  return fecha.toLocaleDateString('es-ES', corto
    ? { weekday: 'short', day: 'numeric', month: 'short' }
    : { weekday: 'long', day: 'numeric', month: 'long' }).replace(',', '') // "jueves, 5…" → "jueves 5…"
}

/** Si la fecha ("aaaa-mm-dd") es mañana. */
export function esManana(iso: string | null | undefined): boolean {
  const d = new Date()
  d.setDate(d.getDate() + 1)
  const manana = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  return iso === manana
}

/**
 * Notarías que atienden la operación en la provincia (y, si se indica, la localidad).
 * Con videoconferencia, las de toda España que la ofrecen; la provincia es opcional y solo ordena.
 */
export async function buscarNotariasMinotaria(operacion: string, provincia: string | null, localidad?: string, videoconferencia = false): Promise<NotariaMinotaria[]> {
  const store = useStore()
  const params = new URLSearchParams({ operacion })
  if (provincia) params.set('provincia', provincia)
  if (localidad && localidad.trim()) params.set('localidad', localidad.trim())
  if (videoconferencia) params.set('videoconferencia', 'true')
  const res = await fetch(`${apiSolicitudes(store)}api/SolicitudWeb/Minotaria/Public/${store.NOTARIA?.id ?? store.NOTARIA?.IdValue}/notarias?${params}`)
  if (!res.ok) throw new Error('HTTP ' + res.status)
  const datos = await res.json()
  return (datos?.notarias ?? []) as NotariaMinotaria[]
}

// --- reCAPTCHA con la clave del grupo de la notaría elegida --------------------
// vue-recaptcha-v3 solo admite una clave por aplicación. Para firmar con la de otro grupo se pinta un widget
// invisible por clave (render explícito) y se ejecuta ese widget.

const widgets = new Map<string, number>()
let cargaScript: Promise<any> | null = null

function grecaptchaListo(): Promise<any> {
  const w = window as any
  if (w.grecaptcha?.render) return new Promise((ok) => w.grecaptcha.ready(() => ok(w.grecaptcha)))
  if (!cargaScript) {
    // Si el asistente ya está cargando el script (vue-recaptcha-v3 también usa render explícito), se espera a ese
    const existente = document.querySelector('script[src*="recaptcha/api.js"]')
    if (existente) {
      cargaScript = new Promise((ok, ko) => {
        const inicio = Date.now()
        const mirar = () => w.grecaptcha?.render ? w.grecaptcha.ready(() => ok(w.grecaptcha))
          : Date.now() - inicio > 10000 ? (cargaScript = null, ko(new Error('No se pudo cargar reCAPTCHA'))) : setTimeout(mirar, 100)
        mirar()
      })
      return cargaScript
    }
    cargaScript = new Promise((ok, ko) => {
      const s = document.createElement('script')
      s.src = 'https://www.google.com/recaptcha/api.js?render=explicit'
      s.async = true
      s.onload = () => w.grecaptcha.ready(() => ok(w.grecaptcha))
      s.onerror = () => { cargaScript = null; ko(new Error('No se pudo cargar reCAPTCHA')) }
      document.head.appendChild(s)
    })
  }
  return cargaScript
}

/** Token de reCAPTCHA v3 firmado con la clave del grupo indicado. */
export async function tokenRecaptchaGrupo(grupo: number | null): Promise<string> {
  const store = useStore()
  const clave = grupo ? store.RecaptchaGroups[grupo] : null
  if (!clave) throw new Error('Grupo de reCAPTCHA sin clave: ' + grupo)
  const g = await grecaptchaListo()
  let id = widgets.get(clave)
  if (id === undefined) {
    const el = document.createElement('div')
    el.style.display = 'none'
    document.body.appendChild(el)
    id = g.render(el, { sitekey: clave, size: 'invisible', badge: 'bottomright' }) as number
    widgets.set(clave, id)
  }
  return g.execute(id, { action: 'email' })
}
